import { useState } from 'react';
import { Button } from '@/components/ui/PageHeader';
import { InputField, TextAreaField, SelectField } from '@/components/ui/FormFields';
import { FileUploadField } from '@/components/ui/FileUploadField';
import {
  createBoardItem,
  updateBoardItem,
  type BoardItem,
  type CreateItemInput,
  type BoardItemType,
  ITEM_TYPE_OPTIONS,
} from '@/api/nostalgia';
import type { UploadResult } from '@/api/upload';

interface ItemFormProps {
  boardId: string;
  item?: BoardItem;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function ItemForm({ boardId, item, onSuccess, onCancel }: ItemFormProps) {
  const isEditing = !!item;

  const [itemType, setItemType] = useState<BoardItemType>(item?.itemType || 'text');
  const [content, setContent] = useState<Record<string, unknown>>(
    (item?.content as Record<string, unknown>) || {}
  );
  const [sortOrder, setSortOrder] = useState(item?.sortOrder || 0);
  const [slotIndex, setSlotIndex] = useState<number | undefined>(item?.slotIndex ?? undefined);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const input: CreateItemInput = {
        itemType,
        content,
        sortOrder,
        slotIndex,
      };

      if (isEditing) {
        await updateBoardItem(boardId, item.id, input);
      } else {
        await createBoardItem(boardId, input);
      }
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save item');
    } finally {
      setIsLoading(false);
    }
  };

  const updateContent = (field: string, value: unknown) => {
    setContent((prev) => ({ ...prev, [field]: value }));
  };

  // Render content fields based on item type
  const renderContentFields = () => {
    switch (itemType) {
      case 'text':
        return (
          <TextAreaField
            label="Memory Text"
            value={String(content.text || '')}
            onChange={(e) => updateContent('text', e.target.value)}
            rows={4}
            placeholder="Write your memory here..."
            required
          />
        );

      case 'image':
        return (
          <>
            <FileUploadField
              label="Image"
              mediaType="image"
              value={content.uploadResult as UploadResult | null}
              onChange={(result) => {
                if (result) {
                  updateContent('url', result.url);
                  updateContent('thumbnailUrl', result.thumbnailUrl || '');
                  updateContent('uploadResult', result);
                } else {
                  updateContent('url', '');
                  updateContent('thumbnailUrl', '');
                  updateContent('uploadResult', null);
                }
              }}
              urlValue={String(content.url || '')}
              onUrlChange={(url) => updateContent('url', url)}
              required
            />
            <InputField
              label="Caption"
              value={String(content.caption || '')}
              onChange={(e) => updateContent('caption', e.target.value)}
              placeholder="Optional caption"
            />
          </>
        );

      case 'video':
        return (
          <>
            <FileUploadField
              label="Video"
              mediaType="video"
              value={content.uploadResult as UploadResult | null}
              onChange={(result) => {
                if (result) {
                  updateContent('url', result.url);
                  updateContent('thumbnailUrl', result.thumbnailUrl || '');
                  updateContent('duration', result.duration);
                  updateContent('uploadResult', result);
                } else {
                  updateContent('url', '');
                  updateContent('thumbnailUrl', '');
                  updateContent('duration', undefined);
                  updateContent('uploadResult', null);
                }
              }}
              urlValue={String(content.url || '')}
              onUrlChange={(url) => updateContent('url', url)}
              required
            />
            <InputField
              label="Caption"
              value={String(content.caption || '')}
              onChange={(e) => updateContent('caption', e.target.value)}
            />
            {content.duration && (
              <p className="text-xs text-gray-500">
                Duration: {Math.floor(Number(content.duration) / 60)}:{(Number(content.duration) % 60).toString().padStart(2, '0')}
              </p>
            )}
          </>
        );

      case 'audio':
        return (
          <>
            <FileUploadField
              label="Audio"
              mediaType="audio"
              value={content.uploadResult as UploadResult | null}
              onChange={(result) => {
                if (result) {
                  updateContent('url', result.url);
                  updateContent('duration', result.duration);
                  updateContent('uploadResult', result);
                } else {
                  updateContent('url', '');
                  updateContent('duration', undefined);
                  updateContent('uploadResult', null);
                }
              }}
              urlValue={String(content.url || '')}
              onUrlChange={(url) => updateContent('url', url)}
              required
            />
            <InputField
              label="Title"
              value={String(content.title || '')}
              onChange={(e) => updateContent('title', e.target.value)}
            />
            <InputField
              label="Caption"
              value={String(content.caption || '')}
              onChange={(e) => updateContent('caption', e.target.value)}
            />
            {content.duration && (
              <p className="text-xs text-gray-500">
                Duration: {Math.floor(Number(content.duration) / 60)}:{(Number(content.duration) % 60).toString().padStart(2, '0')}
              </p>
            )}
          </>
        );

      case 'link':
        return (
          <>
            <InputField
              label="URL"
              type="url"
              value={String(content.url || '')}
              onChange={(e) => updateContent('url', e.target.value)}
              placeholder="https://..."
              required
            />
            <InputField
              label="Title"
              value={String(content.title || '')}
              onChange={(e) => updateContent('title', e.target.value)}
            />
            <TextAreaField
              label="Description"
              value={String(content.description || '')}
              onChange={(e) => updateContent('description', e.target.value)}
              rows={2}
            />
            <InputField
              label="Thumbnail URL"
              type="url"
              value={String(content.thumbnailUrl || '')}
              onChange={(e) => updateContent('thumbnailUrl', e.target.value)}
            />
          </>
        );

      case 'quote':
        return (
          <>
            <TextAreaField
              label="Quote Text"
              value={String(content.text || '')}
              onChange={(e) => updateContent('text', e.target.value)}
              rows={3}
              placeholder="Enter the quote..."
              required
            />
            <InputField
              label="Source"
              value={String(content.source || '')}
              onChange={(e) => updateContent('source', e.target.value)}
              placeholder="Movie, song, person..."
            />
            <InputField
              label="Attribution"
              value={String(content.attribution || '')}
              onChange={(e) => updateContent('attribution', e.target.value)}
              placeholder="Who said it"
            />
          </>
        );

      case 'poll':
        return (
          <>
            <InputField
              label="Question"
              value={String(content.question || '')}
              onChange={(e) => updateContent('question', e.target.value)}
              placeholder="What's your favorite..."
              required
            />
            <TextAreaField
              label="Options (one per line)"
              value={Array.isArray(content.options) ? content.options.join('\n') : ''}
              onChange={(e) => updateContent('options', e.target.value.split('\n').filter(Boolean))}
              rows={4}
              placeholder="Option 1&#10;Option 2&#10;Option 3"
              required
            />
          </>
        );

      case 'then_now':
        return (
          <>
            <div className="grid grid-cols-2 gap-4">
              <FileUploadField
                label="Then Image"
                mediaType="image"
                value={content.thenUploadResult as UploadResult | null}
                onChange={(result) => {
                  if (result) {
                    updateContent('thenUrl', result.url);
                    updateContent('thenUploadResult', result);
                  } else {
                    updateContent('thenUrl', '');
                    updateContent('thenUploadResult', null);
                  }
                }}
                urlValue={String(content.thenUrl || '')}
                onUrlChange={(url) => updateContent('thenUrl', url)}
                required
              />
              <FileUploadField
                label="Now Image"
                mediaType="image"
                value={content.nowUploadResult as UploadResult | null}
                onChange={(result) => {
                  if (result) {
                    updateContent('nowUrl', result.url);
                    updateContent('nowUploadResult', result);
                  } else {
                    updateContent('nowUrl', '');
                    updateContent('nowUploadResult', null);
                  }
                }}
                urlValue={String(content.nowUrl || '')}
                onUrlChange={(url) => updateContent('nowUrl', url)}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <InputField
                label="Then Caption"
                value={String(content.thenCaption || '')}
                onChange={(e) => updateContent('thenCaption', e.target.value)}
              />
              <InputField
                label="Now Caption"
                value={String(content.nowCaption || '')}
                onChange={(e) => updateContent('nowCaption', e.target.value)}
              />
            </div>
          </>
        );

      case 'artifact':
        return (
          <>
            <FileUploadField
              label="Artifact Image"
              mediaType="image"
              value={content.uploadResult as UploadResult | null}
              onChange={(result) => {
                if (result) {
                  updateContent('imageUrl', result.url);
                  updateContent('uploadResult', result);
                } else {
                  updateContent('imageUrl', '');
                  updateContent('uploadResult', null);
                }
              }}
              urlValue={String(content.imageUrl || '')}
              onUrlChange={(url) => updateContent('imageUrl', url)}
              required
            />
            <InputField
              label="Name"
              value={String(content.name || '')}
              onChange={(e) => updateContent('name', e.target.value)}
              placeholder="What is this artifact?"
              required
            />
            <TextAreaField
              label="Description"
              value={String(content.description || '')}
              onChange={(e) => updateContent('description', e.target.value)}
              rows={2}
            />
          </>
        );

      case 'sticker':
        return (
          <>
            <InputField
              label="Sticker ID"
              value={String(content.stickerId || '')}
              onChange={(e) => updateContent('stickerId', e.target.value)}
              required
            />
            <InputField
              label="Sticker URL"
              type="url"
              value={String(content.url || '')}
              onChange={(e) => updateContent('url', e.target.value)}
              required
            />
            <InputField
              label="Name"
              value={String(content.name || '')}
              onChange={(e) => updateContent('name', e.target.value)}
              required
            />
          </>
        );

      default:
        return (
          <TextAreaField
            label="Content (JSON)"
            value={JSON.stringify(content, null, 2)}
            onChange={(e) => {
              try {
                setContent(JSON.parse(e.target.value));
              } catch {
                // Invalid JSON, ignore
              }
            }}
            rows={6}
          />
        );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-3 text-sm text-red-600 bg-red-50 rounded-md">{error}</div>
      )}

      {/* Item Type */}
      <SelectField
        label="Item Type"
        value={itemType}
        onChange={(e) => {
          setItemType(e.target.value as BoardItemType);
          setContent({}); // Reset content when type changes
        }}
        options={ITEM_TYPE_OPTIONS}
        disabled={isEditing}
      />

      {/* Content Fields */}
      <div className="space-y-4">
        <h3 className="font-medium text-gray-900 border-b pb-2">Content</h3>
        {renderContentFields()}
      </div>

      {/* Position */}
      <div className="space-y-4">
        <h3 className="font-medium text-gray-900 border-b pb-2">Position</h3>
        <div className="grid grid-cols-2 gap-4">
          <InputField
            label="Sort Order"
            type="number"
            value={String(sortOrder)}
            onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
            hint="Lower numbers appear first"
          />
          <InputField
            label="Slot Index"
            type="number"
            value={slotIndex !== undefined ? String(slotIndex) : ''}
            onChange={(e) => setSlotIndex(e.target.value ? parseInt(e.target.value) : undefined)}
            hint="For template layouts"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" isLoading={isLoading}>
          {isEditing ? 'Update Item' : 'Add Item'}
        </Button>
      </div>
    </form>
  );
}
